// @ts-check
// ============================================================
// Controllo Area — collectors puri (F1/F2/F3/F4/F5)
// Nessun accesso a DOM né allo `state` globale: tutto è calcolato dagli
// argomenti. Questo rende il modulo type-checkato (S1) e unit-testabile
// (named export) pur restando usabile dai moduli UI legacy via globalThis.
// ============================================================

const ACTION_LIKE = ['Da richiedere', 'Richieste', 'Da eliminare'];

/** Giorni interi da oggi alla data ISO (positivo = futuro). null se invalida. */
export function dayDiff(iso, today = new Date()) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((a - b) / 86400000);
}

const _isActive = (s) => {
  const k = String(s || '')
    .toLowerCase()
    .trim();
  return k === 'sì' || k === 'si';
};
const _isAssigned = (s) => {
  const k = String(s || '')
    .toLowerCase()
    .trim();
  return !!k && k !== 'no' && k !== 'n/a';
};

/**
 * Carico di lavoro per persona (F2): conta clienti e azioni aperte per ruolo.
 * @param {any[]} clients
 */
export function computeWorkload(clients) {
  const list = clients || [];
  /** @type {Map<string, any>} */
  const map = new Map();
  const ensure = (name) => {
    if (!name) return null;
    let e = map.get(name);
    if (!e) {
      e = {
        name,
        comeCpl: 0,
        comePm: 0,
        comeTecnico: 0,
        comeRisorsa: 0,
        clienti: new Set(),
        azioniAperte: 0
      };
      map.set(name, e);
    }
    return e;
  };
  list.forEach((c) => {
    const nm = c.nome;
    if (c.cpl) {
      const e = ensure(c.cpl);
      e.comeCpl++;
      e.clienti.add(nm);
    }
    if (c.pm1) {
      const e = ensure(c.pm1);
      e.comePm++;
      e.clienti.add(nm);
    }
    if (c.pm2) {
      const e = ensure(c.pm2);
      e.comePm++;
      e.clienti.add(nm);
    }
    (c.tecnici || []).forEach((t) => {
      const e = ensure(t);
      if (e) {
        e.comeTecnico++;
        e.clienti.add(nm);
      }
    });
    Object.entries(c.accessi || {}).forEach(([res, stato]) => {
      const e = ensure(res);
      if (!e) return;
      e.comeRisorsa++;
      e.clienti.add(nm);
      if (ACTION_LIKE.includes(String(stato))) e.azioniAperte++;
    });
  });
  return [...map.values()]
    .map((e) => ({
      name: e.name,
      comeCpl: e.comeCpl,
      comePm: e.comePm,
      comeTecnico: e.comeTecnico,
      comeRisorsa: e.comeRisorsa,
      clienti: e.clienti.size,
      azioniAperte: e.azioniAperte
    }))
    .sort(
      (a, b) =>
        b.clienti - a.clienti || b.azioniAperte - a.azioniAperte || a.name.localeCompare(b.name)
    );
}

/**
 * Copertura accessi (F4): % di accessi assegnati che sono attivi ("Sì"),
 * più l'elenco dei clienti con copertura incompleta.
 * @param {any[]} clients
 */
export function computeCoverage(clients) {
  const list = clients || [];
  let assigned = 0;
  let active = 0;
  const gaps = [];
  list.forEach((c) => {
    let cliAssigned = 0;
    let cliActive = 0;
    let pending = 0;
    Object.values(c.accessi || {}).forEach((s) => {
      if (_isAssigned(s)) {
        cliAssigned++;
        assigned++;
        if (_isActive(s)) {
          cliActive++;
          active++;
        } else {
          pending++;
        }
      }
    });
    if (cliAssigned > 0 && pending > 0) {
      gaps.push({
        cliente: c.nome,
        cpl: c.cpl || '',
        assigned: cliAssigned,
        active: cliActive,
        pending,
        pct: Math.round((cliActive / cliAssigned) * 100)
      });
    }
  });
  gaps.sort((a, b) => b.pending - a.pending || a.pct - b.pct);
  return { assigned, active, pct: assigned ? Math.round((active / assigned) * 100) : 100, gaps };
}

/**
 * Pipeline rinnovi contratti (F3): bucket overdue/30/60/90 con conteggi e ore.
 * @param {any[]} clients
 */
export function computeRenewals(clients, today = new Date()) {
  const list = clients || [];
  const rows = [];
  list.forEach((c) => {
    const fine = c.contratto && c.contratto.fine;
    if (!fine) return;
    const g = dayDiff(fine, today);
    if (g === null) return;
    const oreRaw = c.contratto && c.contratto.ore ? parseInt(c.contratto.ore, 10) : 0;
    rows.push({
      cliente: c.nome,
      cpl: c.cpl || '',
      numero: (c.contratto && c.contratto.numero) || '',
      tipo: (c.contratto && c.contratto.tipo) || '',
      fine,
      giorni: g,
      ore: isNaN(oreRaw) ? 0 : oreRaw
    });
  });
  rows.sort((a, b) => a.giorni - b.giorni);
  const between = (min, max) => rows.filter((r) => r.giorni >= min && r.giorni <= max);
  const overdue = rows.filter((r) => r.giorni < 0);
  const d30 = between(0, 30);
  const d60 = between(31, 60);
  const d90 = between(61, 90);
  const sumOre = (arr) => arr.reduce((s, r) => s + (r.ore || 0), 0);
  return {
    rows,
    buckets: {
      overdue: { count: overdue.length, ore: sumOre(overdue), rows: overdue },
      d30: { count: d30.length, ore: sumOre(d30), rows: d30 },
      d60: { count: d60.length, ore: sumOre(d60), rows: d60 },
      d90: { count: d90.length, ore: sumOre(d90), rows: d90 }
    },
    upcoming: rows.filter((r) => r.giorni <= 90)
  };
}

/**
 * Alert a soglia (F5): deriva una lista di avvisi dallo stato + soglie utente.
 * @param {any[]} clients
 * @param {{contractDays?:number, stuckDays?:number, today?:Date}} [opts]
 */
export function computeAlerts(clients, opts) {
  const list = clients || [];
  const o = opts || {};
  const today = o.today || new Date();
  const contractDays = o.contractDays == null ? 30 : o.contractDays;
  const stuckDays = o.stuckDays == null ? 14 : o.stuckDays;
  const out = [];

  const ren = computeRenewals(list, today);
  const overdue = ren.buckets.overdue.count;
  const soon = ren.upcoming.filter((r) => r.giorni >= 0 && r.giorni <= contractDays).length;
  if (overdue > 0)
    out.push({
      key: 'contract-overdue',
      level: 'critical',
      title: 'Contratti scaduti',
      detail: 'Data fine contratto già passata',
      count: overdue,
      view: 'scadenze'
    });
  if (soon > 0)
    out.push({
      key: 'contract-soon',
      level: 'warning',
      title: `Contratti in scadenza (≤ ${contractDays}gg)`,
      detail: 'Rinnovi da pianificare',
      count: soon,
      view: 'scadenze'
    });

  const orphan = list.filter((c) => !c.cpl || !String(c.cpl).trim()).length;
  if (orphan > 0)
    out.push({
      key: 'no-cpl',
      level: 'warning',
      title: 'Clienti senza CPL',
      detail: 'Manca il responsabile assegnato',
      count: orphan,
      view: 'clienti'
    });

  let stuck = 0;
  const ms = stuckDays * 86400000;
  list.forEach((c) =>
    Object.entries(c.accessi || {}).forEach(([res, stato]) => {
      const k = String(stato || '').toLowerCase();
      if (k === 'da richiedere' || k === 'richieste') {
        const meta = c.accessiMeta && c.accessiMeta[res];
        const t = meta && meta.modifiedAt ? new Date(meta.modifiedAt).getTime() : null;
        if (t === null || today.getTime() - t > ms) stuck++;
      }
    })
  );
  if (stuck > 0)
    out.push({
      key: 'access-stuck',
      level: 'warning',
      title: `Accessi fermi (> ${stuckDays}gg)`,
      detail: 'Utenze "Da richiedere"/"Richieste" da sbloccare',
      count: stuck,
      view: 'azioni'
    });

  const cov = computeCoverage(list);
  if (cov.gaps.length > 0)
    out.push({
      key: 'coverage-gap',
      level: 'info',
      title: 'Clienti con copertura accessi incompleta',
      detail: `Copertura media area: ${cov.pct}%`,
      count: cov.gaps.length,
      view: 'controllo'
    });

  const rank = { critical: 0, warning: 1, info: 2 };
  out.sort((a, b) => rank[a.level] - rank[b.level] || b.count - a.count);
  return out;
}

// Esposizione globale per i moduli UI legacy (side-effect import in main.js)
Object.assign(globalThis, {
  areaDayDiff: dayDiff,
  computeWorkload,
  computeCoverage,
  computeRenewals,
  computeAlerts
});
